import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Radio, RadioGroup, Typography } from '@mui/material';
import { RevenueShareFormData } from './RevenueShareFormData';
import TermsLink from '../../../../../../common/components/TermsLink';
import PEGIRatingLink from '../../../../../../common/components/PEGIRatingLink';
import { StyledFormControlLabel } from './revenueShareForm.styles';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { getLatestBuild } from '../../../../../../common/build-utils';

const RevenueShareForm: React.FC = () => {
  const { submission, revenueShareForm, updateRevenueShareForm, baseForm } = React.useContext(SubmissionContext);

  if (!submission) {
    throw new Error('Submission is not available');
  }

  // before full launch developers will end up here again when they can select the exclusive revenue share for 2 months
  // in this case disable ToS and PEGI12 checkboxes, which will already be checked from when they submitted to basic launch
  // don't forget about the special case when starting in full launch from an invited link when all checkboxes are displayed and enabled at once
  const [partiallyAgreed] = useState(
    revenueShareForm.suitableForChildren && (revenueShareForm.doesNotViolate || revenueShareForm.hasCustomContract),
  );

  const latestBuild = getLatestBuild(submission.gameBuilds);
  const qaResult = latestBuild?.qaResult;
  const detectedSDKFunctionality = qaResult?.detectedSDKFunctionality;
  const hasImplementedSDK = !!qaResult?.SDKImplemented;
  const hasImplementedAds = !!(
    detectedSDKFunctionality &&
    hasImplementedSDK &&
    (detectedSDKFunctionality.includes('requestRewardedAd') || detectedSDKFunctionality.includes('requestMidgameAd'))
  );
  const isIframeSubmission = baseForm.gameLoaderType === 'iframe';
  const isBasicLaunch = !submission.isFullLaunch;

  const updateRevenueShareFormInner = (data: RevenueShareFormData) => {
    updateRevenueShareForm(data, !!submission?.isInvited);
  };

  useEffect(() => {
    const updatedForm = { ...revenueShareForm };
    if (isIframeSubmission) {
      updatedForm.makeExclusive = false;
    }
    updatedForm.isEligible = hasImplementedAds;
    updatedForm.hasImplementedSDK = hasImplementedSDK;
    updateRevenueShareFormInner(updatedForm);
    // has to be executed only once when component loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTermsOfServiceAgreement = () => {
    const isInvited = submission?.isInvited ?? false;
    const { doesNotViolate, hasCustomContract } = revenueShareForm;

    const renderNonInvitedOptions = () => (
      <StyledFormControlLabel
        control={
          <Checkbox
            checked={doesNotViolate}
            disabled={partiallyAgreed}
            onChange={(_evt: {}, doesNotViolate: boolean) => {
              updateRevenueShareFormInner({ ...revenueShareForm, doesNotViolate, hasCustomContract: false });
            }}
          />
        }
        label={
          <>
            I agree to the <TermsLink /> of the Developer Portal and acknowledge that they apply to my game submission.
          </>
        }
      />
    );

    const renderInvitedOptions = () => (
      <RadioGroup>
        <StyledFormControlLabel
          value="with-terms"
          control={
            <Radio
              checked={doesNotViolate}
              disabled={partiallyAgreed}
              onChange={(_evt: {}, doesNotViolate: boolean) => {
                updateRevenueShareFormInner({ ...revenueShareForm, doesNotViolate, hasCustomContract: false });
              }}
            />
          }
          label={
            <>
              I publish the game under the <TermsLink /> of the developer portal.
            </>
          }
        />
        <StyledFormControlLabel
          value="with-custom"
          control={
            <Radio
              checked={hasCustomContract}
              disabled={partiallyAgreed}
              onChange={(_evt: {}, hasCustomContract: boolean) => {
                updateRevenueShareFormInner({ ...revenueShareForm, hasCustomContract, doesNotViolate: false });
              }}
            />
          }
          label={
            <>
              I have signed a custom contract with CrazyGames. If this option is mistakenly selected, I accept the general <TermsLink /> of
              the developer portal.
            </>
          }
        />
      </RadioGroup>
    );

    return <>{isInvited ? renderInvitedOptions() : renderNonInvitedOptions()}</>;
  };

  const renderSuitableForChildren = () => {
    const { suitableForChildren } = revenueShareForm;
    return (
      <StyledFormControlLabel
        control={
          <Checkbox
            disabled={partiallyAgreed}
            checked={suitableForChildren}
            onChange={(_evt: {}, suitableForChildren: boolean) => {
              updateRevenueShareFormInner({ ...revenueShareForm, suitableForChildren });
            }}
          />
        }
        label={
          <>
            I confirm that my game is appropriate for players aged 12 and older, and specifically meets the criteria outlined under the{' '}
            <PEGIRatingLink /> rating.
          </>
        }
      />
    );
  };

  const renderExclusiveIncreasedRevenueShare = () => {
    if (
      isIframeSubmission /** Iframes cannot be exclusive, as they are also hosted on their own domain */ ||
      isBasicLaunch /** In basic launch this doesn't apply, it will appear when devs revisit this form before full launch */ ||
      submission.isInvited /** Invited submissions have their own contracts, exclusiveness doesn't apply to them */
    ) {
      return null;
    }
    const { makeExclusive } = revenueShareForm;
    return (
      <>
        <StyledFormControlLabel
          control={
            <Checkbox
              checked={makeExclusive}
              onChange={(_evt: {}, makeExclusive: boolean) => {
                updateRevenueShareFormInner({ ...revenueShareForm, makeExclusive });
              }}
            />
          }
          label={
            <>
              I wish to release my game exclusively on CrazyGames for two months after its Full Launch. In return, I'll receive a 50% boost
              in revenue share during that time. After the exclusivity ends, I'm free to publish it elsewhere.
            </>
          }
        />
      </>
    );
  };

  return (
    <>
      <Typography variant="h5">Ready to submit?</Typography>
      <Box sx={{ px: 2, py: 1 }}>
        {renderTermsOfServiceAgreement()}
        {renderSuitableForChildren()}
        {renderExclusiveIncreasedRevenueShare()}
      </Box>
    </>
  );
};

export default RevenueShareForm;
